/**
 * SelectionControls - Reusable selection control bar
 *
 * Provides a consistent interface for multi-select operations:
 * - Selection count display
 * - Select All button
 * - Clear Selection button
 * - Delete Selected button (with confirmation)
 *
 * Supports two visual variants:
 * - 'inline': Compact horizontal layout for headers
 * - 'bar': Prominent sticky bar for prominent selection UI
 *
 * @module SelectionControls
 */

import { Button, Text } from '@/components/atoms';
import { cn } from '@/lib/utils';

export interface SelectionControlsProps {
  /**
   * Number of currently selected items
   */
  selectedCount: number;

  /**
   * Total number of items available for selection
   */
  totalCount: number;

  /**
   * Callback when Select All is clicked
   */
  onSelectAll: () => void;

  /**
   * Callback when Clear Selection is clicked
   */
  onClearSelection: () => void;

  /**
   * Callback when Delete Selected is clicked
   * Should handle confirmation dialog internally
   */
  onDeleteSelected: () => void;

  /**
   * Whether delete operation is in progress
   * @default false
   */
  isDeleting?: boolean;

  /**
   * Visual variant
   * - 'inline': Compact inline controls for headers
   * - 'bar': Prominent sticky bar
   * @default 'inline'
   */
  variant?: 'inline' | 'bar';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Label for items (e.g., 'photo', 'item')
   * Used in button text: "Delete Selected Photos"
   * @default 'item'
   */
  itemLabel?: string;

  /**
   * Plural form of itemLabel
   * @default itemLabel + 's'
   */
  itemLabelPlural?: string;
}

/**
 * SelectionControls component
 *
 * Displays selection state and provides actions for multi-select operations.
 * Automatically hides when totalCount is 0.
 */
export function SelectionControls({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  isDeleting = false,
  variant = 'inline',
  className,
  itemLabel = 'item',
  itemLabelPlural,
}: SelectionControlsProps) {
  // Don't render if there are no items
  if (totalCount === 0) {
    return null;
  }

  const pluralLabel = itemLabelPlural || `${itemLabel}s`;
  const allSelected = selectedCount === totalCount;
  const noneSelected = selectedCount === 0;

  // Selection count text
  const countText = noneSelected
    ? 'None selected'
    : `${selectedCount} ${selectedCount === 1 ? itemLabel : pluralLabel} selected`;

  if (variant === 'bar') {
    // Prominent sticky bar - only show when items are selected
    if (noneSelected) {
      return null;
    }

    return (
      <div
        className={cn(
          'sticky top-0 z-10 bg-surface-1 border border-border rounded-lg p-4 mb-4',
          'flex flex-wrap items-center gap-4',
          className
        )}
      >
        <div className="text-sm font-medium text-foreground">{countText}</div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onSelectAll}
            variant="secondary"
            size="md"
            disabled={allSelected}
            aria-label={`Select all ${pluralLabel}`}
          >
            Select All
          </Button>
          <Button
            onClick={onClearSelection}
            variant="secondary"
            size="md"
            aria-label="Clear selection"
          >
            Clear
          </Button>
          <Button
            onClick={onDeleteSelected}
            variant="danger"
            size="md"
            disabled={isDeleting}
            aria-label={`Delete ${selectedCount} selected ${selectedCount === 1 ? itemLabel : pluralLabel}`}
          >
            {isDeleting ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </div>
      </div>
    );
  }

  // Inline compact controls
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Text variant="muted" className="text-sm">
        {countText}
      </Text>
      <Button
        onClick={onSelectAll}
        disabled={allSelected}
        variant="secondary"
        size="sm"
        aria-label={`Select all ${pluralLabel}`}
      >
        Select All
      </Button>
      <Button
        onClick={onClearSelection}
        disabled={noneSelected}
        variant="secondary"
        size="sm"
        aria-label="Clear selection"
      >
        Clear
      </Button>
      <Button
        onClick={onDeleteSelected}
        disabled={noneSelected || isDeleting}
        variant="danger"
        size="sm"
        aria-label={`Delete ${selectedCount} selected ${selectedCount === 1 ? itemLabel : pluralLabel}`}
      >
        {isDeleting ? 'Deleting...' : `Delete Selected (${selectedCount})`}
      </Button>
    </div>
  );
}
